import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

export type ThemeMode = "light" | "dark";

@Injectable({
  providedIn: "root",
})
export class ThemePreferenceService {
  readonly themeStorageKey = "selectedTheme";
  private readonly themeMode$ = new BehaviorSubject<ThemeMode>(this.readSavedTheme());

  get currentTheme(): ThemeMode {
    return this.themeMode$.value;
  }

  get themeChanges$(): Observable<ThemeMode> {
    return this.themeMode$.asObservable();
  }

  initializeFromStorage(): ThemeMode {
    const savedTheme = this.readSavedTheme();
    this.setTheme(savedTheme, false);
    return savedTheme;
  }

  setTheme(theme: ThemeMode, persist: boolean = true): void {
    const normalizedTheme: ThemeMode = theme === "dark" ? "dark" : "light";
    document.body.classList.remove("theme-light", "theme-dark", "darkmode");
    document.body.classList.add(
      normalizedTheme === "dark" ? "theme-dark" : "theme-light"
    );

    if (persist) {
      localStorage.setItem(this.themeStorageKey, normalizedTheme);
    }

    this.themeMode$.next(normalizedTheme);
  }

  toggleTheme(): ThemeMode {
    const nextTheme: ThemeMode = this.currentTheme === "dark" ? "light" : "dark";
    this.setTheme(nextTheme, true);
    return nextTheme;
  }

  private readSavedTheme(): ThemeMode {
    const savedTheme = (localStorage.getItem(this.themeStorageKey) || "")
      .toLowerCase()
      .trim();
    return savedTheme === "dark" ? "dark" : "light";
  }
}
